import styled from "styled-components";
import { SHOW_ISLE_OF_MAN } from "../../site/flags";

const ROOT_MAX_WIDTH = '72rem';

const PageWrapper = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
`;

const Card = styled.article`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: clamp(1.25rem, 4vw, 2.5rem);
    max-width: ${ROOT_MAX_WIDTH};
    width: 100%;
    box-sizing: border-box;
`;

const MetaHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding-bottom: 1.5rem;
    border-bottom: 2px solid ${({ theme }) => theme.colour.COLOUR_MID};
`;

const Header = styled.h5`
    margin: 0;
    font-family: ${({ theme }) => theme.font.FONT_SANS_RED_HAT_DISPLAY};
    font-size: 0.8rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colour.LIGHT_GREY};
`;

const Title = styled.h1`
    margin: 0.35rem 0 0;
    font-family: ${({ theme }) => theme.font.FONT_SERIF_ROBOTO_SERIF};
    font-weight: 700;
    font-size: 1.5em;
    line-height: 1.1;
    color: ${({ theme }) => theme.colour.OFF_WHITE};
`;

const UpdatedAt = styled.p`
    margin: 0.6rem 0 0;
    font-family: ${({ theme }) => theme.font.FONT_SANS_RED_HAT_DISPLAY};
    font-size: 0.9rem;
    color: ${({ theme }) => theme.colour.LIGHT_GREY};
`;

const ContentStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.15rem;
    padding-top: 2rem;
    max-width: 68ch;
`;

const SectionTitle = styled.h2`
    margin: 0.5rem 0 0;
    font-family: ${({ theme }) => theme.font.FONT_SERIF_ROBOTO_SERIF};
    font-weight: 700;
    font-size: 1.3rem;
    line-height: 1.3;
    color: ${({ theme }) => theme.colour.OFF_WHITE};
`;

const Paragraph = styled.p`
    margin: 0;
    font-family: ${({ theme }) => theme.font.FONT_SANS_RED_HAT_DISPLAY};
    font-size: 1rem;
    line-height: 1.7;
    color: ${({ theme }) => theme.colour.OFF_WHITE};
`;

export default function Terms() {
    return (
        <PageWrapper>
            <Card>
                <MetaHeader>
                    <Header>Legal</Header>
                    <Title>Terms &amp; Conditions</Title>
                    <UpdatedAt>Last updated 16 September 2026</UpdatedAt>
                </MetaHeader>
                <ContentStack>
                    <Paragraph>
                        These terms and conditions govern the provision of services by O'Neill Group LLC
                        ("O'Neill Software", "we", "us") to any client that engages us for engineering,
                        software, or hardware services ("Client", "you"). By engaging our services, you
                        agree to be bound by these terms.
                    </Paragraph>

                    <SectionTitle>Payment Terms</SectionTitle>
                    <Paragraph>
                        Invoices are due for payment no later than seven (7) days from the date of issue.
                        Late payments may result in the suspension of ongoing work until the outstanding
                        balance is settled.
                    </Paragraph>

                    <SectionTitle>Service Levels</SectionTitle>
                    <Paragraph>
                        We do not guarantee any particular service level, response time, or uptime unless
                        such a commitment is explicitly agreed in a signed contract or statement of work
                        with the Client.
                    </Paragraph>

                    <SectionTitle>Ownership of Software</SectionTitle>
                    <Paragraph>
                        All software, source code, and associated intellectual property developed by
                        O'Neill Software remains the sole property of O'Neill Software until all
                        payments due under the relevant engagement have been paid in full. Prior to full
                        payment, this software is provided under a proprietary, non-transferable license
                        for the Client's internal evaluation and use only, with all rights reserved by
                        O'Neill Software. Upon receipt of full payment, ownership transfers to the Client
                        as agreed in the applicable contract.
                    </Paragraph>

                    <SectionTitle>Confidentiality</SectionTitle>
                    <Paragraph>
                        Each party agrees to keep confidential any non-public information disclosed by the
                        other party in connection with an engagement, and to use it only for the purposes
                        of that engagement. This obligation survives termination of the engagement.
                    </Paragraph>

                    <SectionTitle>Warranties and Disclaimer</SectionTitle>
                    <Paragraph>
                        Services are provided on an "as is" basis. Except as expressly agreed in a signed
                        contract, O'Neill Software makes no other warranties, express or implied, including
                        any warranty of merchantability or fitness for a particular purpose.
                    </Paragraph>

                    <SectionTitle>Limitation of Liability</SectionTitle>
                    <Paragraph>
                        To the maximum extent permitted by law, O'Neill Software shall not be liable for
                        any indirect, incidental, or consequential damages arising from the use of our
                        services, except where such liability cannot be excluded by law.
                    </Paragraph>

                    <SectionTitle>Termination</SectionTitle>
                    <Paragraph>
                        Either party may terminate an engagement on written notice if the other party
                        materially breaches these terms and fails to remedy that breach within a
                        reasonable period. Fees for work performed up to the date of termination remain
                        payable.
                    </Paragraph>

                    <SectionTitle>Force Majeure</SectionTitle>
                    <Paragraph>
                        Neither party is liable for any delay or failure to perform caused by
                        circumstances beyond its reasonable control, including natural disasters,
                        internet or infrastructure outages, or other events of force majeure.
                    </Paragraph>

                    <SectionTitle>Governing Law</SectionTitle>
                    <Paragraph>
                        {SHOW_ISLE_OF_MAN
                            ? 'These terms are governed by the laws of the Isle of Man, and any disputes arising from them are subject to the exclusive jurisdiction of the courts of the Isle of Man.'
                            : 'These terms are governed by the laws of the relevant jurisdiction, and any disputes arising from them are subject to the exclusive jurisdiction of the courts of that jurisdiction.'}
                    </Paragraph>

                    <SectionTitle>Changes to These Terms</SectionTitle>
                    <Paragraph>
                        We may update these terms from time to time. Continued use of our services after
                        any such changes constitutes acceptance of the new terms.
                    </Paragraph>

                    <SectionTitle>Contact</SectionTitle>
                    <Paragraph>
                        Questions about these terms can be sent to engineering@oneillsoft.com.
                    </Paragraph>
                </ContentStack>
            </Card>
        </PageWrapper>
    );
}
